import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { client } from '../../../lib/amplifyClient';
import TherapistPracticeDrawer, {
  isTherapistPracticeRole,
} from '../../admin/org-dashboard/TherapistPracticeDrawer';
import type { OrgDashboardMenuId } from '../../admin/org-dashboard/therapistJobsAndRoutes';
import GlobalLayout from '../../common/GlobalLayout';

type CommunityLayoutProps = {
  children: React.ReactNode;
};

/** Role-aware chrome for `/community/*`: parent LeftNav vs therapist practice drawer. */
export default function CommunityLayout({ children }: CommunityLayoutProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState<string | undefined>();
  const isClinical = isTherapistPracticeRole(user?.role);

  useEffect(() => {
    const orgId = user?.organizationId;
    if (!orgId) {
      setOrgName(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await client.models.Organization.get({ id: orgId }, { authMode: 'apiKey' });
        if (!cancelled) setOrgName(res.data?.name ?? undefined);
      } catch {
        if (!cancelled) setOrgName(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.organizationId]);

  const navigateToMenu = (id: OrgDashboardMenuId) => {
    navigate(`/org-admin-dashboard?section=${encodeURIComponent(id)}`);
  };

  if (isClinical) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] bg-gray-50">
        <TherapistPracticeDrawer
          role={user?.role}
          selectedMenu=""
          orgName={orgName}
          onSelect={navigateToMenu}
        />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    );
  }

  return <GlobalLayout>{children}</GlobalLayout>;
}

export function communityHubBackPath(role?: string | null): { label: string; href: string } {
  if (isTherapistPracticeRole(role)) {
    return { label: 'Back to dashboard', href: '/org-admin-dashboard' };
  }
  return { label: 'Back to kids', href: '/kid-profile' };
}
